"use client";

import { StoryblokComponent } from "@frend-digital/storyblok";
import type { ISbStoryData } from "@storyblok/react/rsc";
import clsx from "clsx";
import React, { memo, useCallback, useState } from "react";
import { useProducts } from "@/features/ProductListingPage/components/ProductListing/ListingContext";
import type { ProductListingPageStoryblok } from "@/generated/sb/sb-types";
import useIntersection from "@/hooks/useIntersection";
import type { ProductCard as ProductCardType } from "@/lib/centra/formatters";
import { ViewItemListEvent } from "@/lib/utils/gtm";
import ProductCard from "../ProductCard";
import { ProductCardListSkeleton } from "../ProductCardList";
import ProductGrid from "../ProductGrid";
import styles from "./index.module.css";

const formatProductsForPlp = (
  pages: Array<{ products: ProductCardType[] }> | undefined,
  bloks: ProductListingPageStoryblok["listingBloks"],
  count: number,
) => {
  const formattedProducts: {
    products: ProductCardType[];
    editorial?: Required<ProductListingPageStoryblok>["listingBloks"][number][];
  }[] = [];

  if (!pages) return formattedProducts;

  const allProducts = pages.flatMap(page => page.products);
  for (let i = 0; i < allProducts.length; i += count) {
    const sectionIndex = Math.floor(i / count);
    formattedProducts.push({
      products: allProducts.slice(i, i + count),
      editorial: bloks?.slice(sectionIndex * 2, sectionIndex * 2 + 2),
    });
  }

  return formattedProducts;
};

const ProductListSection = memo(
  ({
    products,
    priority: initialPriority,
  }: {
    products: ProductCardType[];
    priority?: boolean;
  }) => {
    return (
      <>
        {products.map((product, index) => {
          const priority = !initialPriority && index < 4;
          return (
            <ProductCard
              product={product}
              key={`product-${product?.id}-${product?.core?.slug}`}
              priority={priority}
            />
          );
        })}
      </>
    );
  },
);

ProductListSection.displayName = "ProductListSection";

const ProductsSection = ({
  gridSize,
  preview,
  story,
}: {
  gridSize: number;
  preview: boolean;
  story?: ISbStoryData<ProductListingPageStoryblok>;
}) => {
  const { query, firstPage, isLoading } = useProducts();

  const { data: result, fetchNextPage, isFetching, hasNextPage } = query;

  const data = result as typeof result | undefined;

  const formattedSections = formatProductsForPlp(
    data?.pages,
    story?.content.listingBloks,
    gridSize === 3 ? 6 : 8,
  );

  return (
    <>
      <section>
        <ProductGrid
          id="grid"
          className={clsx(gridSize === 3 ? styles.grid3 : styles.grid4)}
        >
          {formattedSections.map((section, idx) => (
            <React.Fragment key={`section-${idx}`}>
              {firstPage && story?.content.depictCategory && (
                <ViewItemListEvent
                  items={firstPage.products}
                  id={story.content.depictCategory}
                  name={story.full_slug}
                />
              )}
              <ProductListSection
                key={`products-${idx}`}
                products={section.products as ProductCardType[]}
                priority={idx < 4}
              />
              {section.editorial && (
                <ProductEditorial
                  key={`editorial-${idx}`}
                  section={section}
                  gridSize={gridSize}
                  preview={preview}
                />
              )}
            </React.Fragment>
          ))}
        </ProductGrid>
      </section>
      {isLoading && <ProductCardListSkeleton />}
      {isFetching && <ProductCardListSkeleton />}
      <div className={styles.paginator}>
        {hasNextPage && <LoadMore fetchNextPage={fetchNextPage} />}
        <p className={styles.description}>
          Showing{" "}
          {data?.pages?.reduce((acc, curr) => acc + curr.products.length, 0)} of{" "}
          {data?.pages[0]?.total}
        </p>
      </div>
    </>
  );
};

export default ProductsSection;

const LoadMore = ({
  fetchNextPage,
}: {
  fetchNextPage: () => Promise<unknown>;
}) => {
  const [loadMore, setLoadMore] = useState(false);

  return (
    <>
      {!loadMore ? (
        <button
          className={styles.loadMore}
          onClick={async () => {
            setLoadMore(true);
          }}
        >
          Load more products
        </button>
      ) : (
        <Sentinel fetchNextPage={fetchNextPage} />
      )}
    </>
  );
};

const Sentinel = ({
  fetchNextPage,
}: {
  fetchNextPage: () => Promise<unknown>;
}) => {
  const handleInView = useCallback(
    (entries: IntersectionObserverEntry[], _observer: IntersectionObserver) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        fetchNextPage();
      }
    },
    [fetchNextPage],
  );

  const { Ref } = useIntersection(handleInView);

  return (
    <span className={styles.sentinel} aria-hidden id="sentinel" ref={Ref} />
  );
};
const ProductEditorial = ({
  section,
  gridSize,
  preview,
}: {
  section: {
    products: ProductCardType[];
    editorial?: Required<ProductListingPageStoryblok>["listingBloks"][number][];
  };
  gridSize: number;
  preview: boolean;
}) => {
  return (
    <>
      {section?.editorial?.map(
        (editorialBlok, key) =>
          key * 2 < section?.products.length / gridSize && (
            <div
              className={clsx(
                styles.editorial,
                gridSize === 3 ? styles.sectionGrid3 : styles.sectionGrid4,
              )}
              key={key}
              style={{
                gridRow: 3 * key + 2,
              }}
            >
              <StoryblokComponent blok={editorialBlok} preview={preview} />
            </div>
          ),
      )}
    </>
  );
};
